/**
 * 科目・キャラクター・アイテム・ガチャの編集画面の共通部品(docs/20 §5)。
 *
 * - `useCatalogStore`: 下書きの読み込み・保存(cmsSaveDrafts。expectedRevision 付き)。
 *   通信エラーで押し直したときは同じ操作ID(冪等キー)で再送する。
 * - 検証メッセージの表示、保存ボタン・結果の表示。
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { CmsError, newOperationId, toCmsError } from '../../services/cms';
import {
  applySaveResults,
  buildSaveOps,
  EMPTY_DRAFTS,
  MAX_OPS_PER_SAVE,
  type CatalogChange,
  type CatalogDraftReader,
  type CatalogDraftSaver,
  type CatalogDrafts,
  type EntityIssues,
} from '../../services/catalog';

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export type SaveOutcome =
  { readonly ok: true; readonly saved: number } | { readonly ok: false; readonly error: CmsError };

export interface CatalogStore {
  readonly loadState: LoadState;
  readonly loadError: string | null;
  readonly drafts: CatalogDrafts;
  reload(): Promise<void>;
  save(changes: readonly CatalogChange[]): Promise<SaveOutcome>;
}

/** 下書きの読み込みと保存。`active` になったとき初めて読む。 */
export function useCatalogStore(
  reader: CatalogDraftReader,
  saver: CatalogDraftSaver,
  active: boolean,
): CatalogStore {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<CatalogDrafts>(EMPTY_DRAFTS);
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  // 通信エラーのあと同じ内容を押し直したら、同じ操作IDで送る(サーバー側で二重に書かない)。
  const retry = useRef<{ key: string; operationId: string } | null>(null);

  const reload = useCallback(async () => {
    setLoadState('loading');
    setLoadError(null);
    try {
      const loaded = await reader.load();
      setDrafts(loaded);
      setLoadState('ready');
    } catch (cause) {
      setLoadError(String(cause));
      setLoadState('error');
    }
  }, [reader]);

  useEffect(() => {
    if (active && loadState === 'idle') void reload();
  }, [active, loadState, reload]);

  const save = useCallback(
    async (changes: readonly CatalogChange[]): Promise<SaveOutcome> => {
      if (changes.length === 0) return { ok: true, saved: 0 };
      if (changes.length > MAX_OPS_PER_SAVE) {
        return {
          ok: false,
          error: new CmsError(
            'unknown',
            `1回に保存できるのは ${MAX_OPS_PER_SAVE} 件までです(${changes.length} 件)。絞り込んで分けてください。`,
          ),
        };
      }
      const current = draftsRef.current;
      const ops = buildSaveOps(current, changes);
      const key = JSON.stringify(ops);
      const operationId = retry.current?.key === key ? retry.current.operationId : newOperationId();
      try {
        const results = await saver.save(ops, operationId);
        retry.current = null;
        const next = applySaveResults(draftsRef.current, changes, results);
        draftsRef.current = next;
        setDrafts(next);
        return { ok: true, saved: changes.length };
      } catch (cause) {
        const error = toCmsError(cause);
        retry.current = error.code === 'network' ? { key, operationId } : null;
        return { ok: false, error };
      }
    },
    [saver],
  );

  return { loadState, loadError, drafts, reload, save };
}

export function IssueList({ issues }: { readonly issues: EntityIssues }) {
  if (issues.errors.length === 0 && issues.warnings.length === 0) return null;
  return (
    <div className="field-issues">
      {issues.errors.length > 0 && (
        <ul className="issue-list" aria-label="検証エラー">
          {issues.errors.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      )}
      {issues.warnings.length > 0 && (
        <ul className="issue-list issue-list--warn" aria-label="警告">
          {issues.warnings.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** 保存の結果(競合は「ほかの人が更新しました。読み直してください」)。 */
export function SaveNotice({
  outcome,
  onReload,
}: {
  readonly outcome: SaveOutcome | null;
  readonly onReload: () => void;
}) {
  if (outcome === null) return null;
  if (outcome.ok) {
    return (
      <div className="notice notice--ok" role="status">
        保存しました({outcome.saved} 件)。公開するには「公開」でプレビュー版を作ってください。
      </div>
    );
  }
  const { error } = outcome;
  return (
    <div className="notice notice--error" role="alert" data-code={error.code}>
      <div>{error.message}</div>
      {error.code === 'revision-conflict' && (
        <button className="button button--small" type="button" onClick={onReload}>
          読み直す
        </button>
      )}
      {error.errors.length > 0 && (
        <ul className="issue-list">
          {error.errors.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** 一覧の読み込み状態(読み込み中・失敗・空)。 */
export function LoadGate({
  store,
  children,
}: {
  readonly store: CatalogStore;
  readonly children: ReactNode;
}) {
  if (store.loadState === 'error') {
    return (
      <div className="pane pane--page">
        <div className="notice notice--error" role="alert">
          下書きを読み込めませんでした: {store.loadError}
        </div>
        <button className="button" type="button" onClick={() => void store.reload()}>
          もう一度読む
        </button>
      </div>
    );
  }
  if (store.loadState !== 'ready') {
    return (
      <div className="pane pane--page">
        <p className="muted">下書きを読み込み中…</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function ReadOnlyNote({ editable }: { readonly editable: boolean }) {
  if (editable) return null;
  return <p className="muted">閲覧のみ(編集は editor 以上)。</p>;
}

/** 合計エラー件数(画面上部の目安)。 */
export function CatalogSummary({
  errors,
  warnings,
}: {
  readonly errors: number;
  readonly warnings: number;
}) {
  return (
    <span
      className={errors > 0 ? 'summary summary--error' : 'summary'}
      data-testid="catalog-summary"
    >
      下書き全体: エラー {errors} 件 / 警告 {warnings} 件
    </span>
  );
}

/** 文字列 → 数値(空や不正は NaN のまま。検証で拾う)。 */
export function toNumber(value: string): number {
  return value.trim() === '' ? Number.NaN : Number(value);
}

export function numberText(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

/**
 * 数値の入力欄。入力途中(`0.` など)を壊さないよう文字列を手元に持ち、数値として読めたら知らせる
 * (空・不正は NaN を知らせ、検証のエラーで見せる)。
 */
export function NumberInput({
  value,
  onChange,
  label,
  className = 'input input--num',
}: {
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly label: string;
  readonly className?: string;
}) {
  const [text, setText] = useState(numberText(value));
  useEffect(() => {
    setText((current) => (Object.is(toNumber(current), value) ? current : numberText(value)));
  }, [value]);
  return (
    <input
      className={className}
      inputMode="decimal"
      aria-label={label}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        onChange(toNumber(event.target.value));
      }}
    />
  );
}
